import React, { useState, useEffect, useMemo } from "react";
import moment from "moment-timezone";
import { FormLabel } from "react-bootstrap";

const formatDateTimeLocal = (date) => {
  if (!date) return "";
  return moment(date).format("YYYY-MM-DDTHH:mm");
};

const BidTimingSelector = ({
  startDate,
  endDate,
  auctionDuration,
  onStartDateChange,
  onEndDateChange,
  errors,
  createdAt,
  editEndDate,
}) => {
  const [isManualEndDate, setIsManualEndDate] = useState(false);
  const [quickSetStart, setQuickSetStart] = useState("");
  const [quickSetEnd, setQuickSetEnd] = useState("");

  useEffect(() => {
    if (startDate && !isManualEndDate && auctionDuration && !editEndDate) {
      const newEndDate = moment(startDate)
        .add(auctionDuration, "days")
        .toDate();
      onEndDateChange(formatDateTimeLocal(newEndDate));
    }
  }, [startDate, auctionDuration, isManualEndDate, onEndDateChange]);

  const calculatedDuration = useMemo(() => {
    if (!startDate || !endDate) return "N/A";
    const start = moment(startDate);
    const end = moment(endDate);
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
      return "Invalid date range";
    }
    const duration = moment.duration(end.diff(start));

    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();

    return `${days}d ${hours}h ${minutes}m`;
  }, [startDate, endDate]);

  const handleQuickSetStart = (e) => {
    const type = e.target.value;
    setQuickSetStart(type);

    if (!type) return;

    let newStartDate;
    const now = moment();

    switch (type) {
      case "24h":
        newStartDate = moment(createdAt).add(24, "hours").toDate();
        break;
      case "today":
        newStartDate = now.hour(12).minute(0).second(0).toDate();
        break;
      case "tomorrow":
        newStartDate = now.add(1, "day").hour(12).minute(0).second(0).toDate();
        break;
      default:
        return;
    }
    onStartDateChange(formatDateTimeLocal(newStartDate));
  };

  const handleQuickSetEnd = (e) => {
    const days = parseInt(e.target.value, 10);
    setQuickSetEnd(days);
    if (startDate && !isNaN(days)) {
      const newEndDate = moment(startDate).add(days, "days").toDate();
      onEndDateChange(formatDateTimeLocal(newEndDate));
    }
  };

  return (
    <div className="mt-4">
      <h4 className="text-black mb-3 fw-600">Bid Timing</h4>
      <div>
        <div className="mb-3">
          {auctionDuration ? (
            <h4>
              Set Auction Duration: User requested {auctionDuration} days.
            </h4>
          ) : (
            <h4>Set auction duration (no preference from user).</h4>
          )}

          {createdAt && (
            <p
              className="text-muted fw-bold mb-0"
              style={{ fontSize: "0.95rem" }}
            >
              User submitted auction on:{" "}
              <span className="text-dark">
                {moment(createdAt).format("MM/DD/YYYY [at] h:mm A")}
              </span>
            </p>
          )}
        </div>
      </div>
      <div className="mb-3">
        <FormLabel className="text-dark fw-600 mb-2">Start Date</FormLabel>
        <div className="d-flex align-items-center gap-2">
          <input
            type="datetime-local"
            name="bidstart"
            className="form-control"
            value={startDate}
            min={formatDateTimeLocal(new Date())}
            onChange={(e) => onStartDateChange(e.target.value)}
          />
          <select
            className="form-select w-auto flex-shrink-0"
            value={quickSetStart}
            onChange={handleQuickSetStart}
          >
            <option value="" disabled>
              Quick Set
            </option>
            <option value="today">Today at 12pm</option>
            <option value="tomorrow">Tomorrow at 12pm</option>
            {createdAt && <option value="24h">24 Hours from Submission</option>}
          </select>
        </div>
        {errors?.bidstart && (
          <p className="d-flex flex-start text-danger error_msg mb-1 mb-md-0">
            {errors.bidstart}
          </p>
        )}
      </div>

      <div className="mb-3">
        <FormLabel className="text-dark fw-600 mb-2">End Date</FormLabel>
        <div className="d-flex align-items-center gap-2">
          <input
            type="datetime-local"
            name="bidend"
            className="form-control"
            value={endDate}
            min={startDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            readOnly={!isManualEndDate}
          />

          {isManualEndDate && (
            <select
              className="form-select w-auto flex-shrink-0"
              onChange={handleQuickSetEnd}
            >
              <option value="" disabled selected>
                Set Duration
              </option>
              <option value="3">3 Days</option>
              <option value="5">5 Days</option>
              <option value="7">7 Days</option>
              <option value="15">15 Days</option>
            </select>
          )}

          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="manualEndDateCheck"
              checked={isManualEndDate}
              onChange={(e) => setIsManualEndDate(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="manualEndDateCheck">
              Set Manually
            </label>
          </div>
        </div>
        {errors?.bidend && (
          <p className="d-flex flex-start text-danger error_msg mb-1 mb-md-0">
            {errors.bidend}
          </p>
        )}
      </div>

      <div className="col-12 mt-2">
        <p className="text-muted">
          Auction will last: <strong>{calculatedDuration}</strong>
        </p>
      </div>
    </div>
  );
};

export default BidTimingSelector;
